import React from 'react';
import { Column } from 'react-table';
import SessionTable, {
  ResponseGuard,
  SessionTableInstanceProps
} from '../index';
import { CustomSessionCell, RotatingQuestionCell } from '../../cells';
import Tooltip from '../../../tooltip';
import Icon from '../../../../atoms/icon';
import { faInfo } from '@fortawesome/pro-solid-svg-icons';

import {
  formatFlag as formatHeader,
  ICustomProgram,
  ICustomSession,
  getRotatingQuestionNode
} from '../../../../../libs/composer';

/**
 * Formats a flow type value for display.
 * Converts values like "regular", "recheck", "week1day1" to readable format.
 *
 * @param flowType - The flow type value from session._data.subset
 * @returns Formatted flow type string
 */
const formatFlowType = (flowType: string | undefined | null): string => {
  if (!flowType) {
    return 'Regular';
  }
  // Replace underscores with spaces and capitalize
  return flowType
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2') // Insert space between camelCase
    .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize first letter of each word
};

/**
 * Extracts the data keys from the custom program source.
 *
 * @param source - The source object from the custom program
 * @returns The list of data keys
 */
const getDataKeysFromSource = (source: ICustomProgram['source']): string[] => {
  const keys = new Set<string>();
  const schema = JSON.parse(source);
  let subsets = null;
  if (!Array.isArray(schema.links) && typeof schema.links === 'object') {
    subsets = Object.keys(schema.links);
  }
  if (subsets !== null) {
    for (const subset of subsets) {
      for (const link of schema.links[subset]) {
        if (link.store && typeof link.store === 'object') {
          for (const key of Object.keys(link.store)) {
            keys.add(key);
          }
        }
      }
    }
  } else {
    for (const link of schema.links) {
      if (link.store && typeof link.store === 'object') {
        for (const key of Object.keys(link.store)) {
          keys.add(key);
        }
      }
    }
  }
  return Array.from(keys);
};

/**
 * Checks if a store key corresponds to a rotating question.
 * Looks for links from the rotating question node that store to this key.
 *
 * @param source - The source object from the custom program
 * @param key - The store key to check
 * @returns True if the key corresponds to a rotating question
 */
const isRotatingQuestionKey = (
  source: ICustomProgram['source'],
  key: string
): boolean => {
  if (!source) {
    return false;
  }
  try {
    const rotatingNode = getRotatingQuestionNode(source);
    if (!rotatingNode) {
      return false;
    }
    const schema = JSON.parse(source);
    let linksToCheck: any[] = [];
    if (!Array.isArray(schema.links) && typeof schema.links === 'object') {
      // Multiple flow types
      for (const subset of Object.keys(schema.links)) {
        linksToCheck = linksToCheck.concat(schema.links[subset]);
      }
    } else {
      linksToCheck = schema.links || [];
    }
    // Check if any link from the rotating node stores to this key
    for (const link of linksToCheck) {
      if (
        link.from === rotatingNode.nodeKey &&
        link.store &&
        typeof link.store === 'object' &&
        Object.keys(link.store).includes(key)
      ) {
        return true;
      }
    }
  } catch {
    // Silently fail
  }
  return false;
};

const GenericTable: React.FC<SessionTableInstanceProps> = (
  props
): JSX.Element => {
  const source: ICustomProgram['source'] = props.customProgram?.source;
  const customDataKeys = getDataKeysFromSource(source);
  const columns: Column<ICustomSession>[] = [];

  // Add Flow Type column at the beginning
  columns.push({
    Header: (
      <Tooltip content="The flow type determines which set of questions and logic are used for this session. For custom programs with multiple flow types (like time-based flows), this shows which specific flow was used (e.g., 'regular', 'week1day1', 'monthly').">
        <Icon icon={faInfo} />
        <span className="ml-4">Flow Type</span>
      </Tooltip>
    ),
    id: 'flowType',
    Cell: ResponseGuard(({ row: { original: session } }) => {
      const flowType = session._data?.subset as string | undefined | null;
      const formatted = formatFlowType(flowType);
      return <span>{formatted}</span>;
    })
  });

  for (const key of customDataKeys) {
    const isRotating = source ? isRotatingQuestionKey(source, key) : false;
    columns.push({
      Header: formatHeader(key),
      id: key,
      Cell: ResponseGuard(
        isRotating
          ? RotatingQuestionCell(
              key as keyof ICustomSession,
              props.customProgram
            )
          : CustomSessionCell(key as keyof ICustomSession)
      )
    });
  }
  return (
    <div style={{ height: '100%' }}>
      <style>{`
        .session-table-container .bp3-card {
          max-height: 500px;
          overflow-y: auto;
        }
      `}</style>
      <div className="session-table-container">
        <SessionTable<ICustomSession>
          url="/composer/data/"
          columns={columns}
          {...props}
        />
      </div>
    </div>
  );
};

export default GenericTable;
